import Grid from '@mui/material/Grid'
import { OptProject, useOptProjects, useOptProjectsDeleteMutation } from 'api/optProjects/optProjects.api'
import { formatDateTimeToLocalizedString } from 'helpers/dateTime.helper/dateTime.helper'
import { getSandboxOptStatus, getSandboxOptType } from 'helpers/sandbox.helper/sandbox.helper'
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import alertStore from 'store/alert/alert'
import NewButton from 'ui/atoms/NewButton/NewButton'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import DefaultTable, { LegacyColumn } from 'ui/components/CustomTable/CustomTable'
import HeaderPeriodPicker from 'ui/components/HeaderPeriodPicker/HeaderPeriodPicker'
import StatusComponent from 'ui/components/StatusComponent/StatusComponent'
import CreateSandboxPopover from '../CreateSandboxPopover/CreateSandboxPopover'

type ProjectStatus = `delete` | `create` | undefined

type SandboxTableContentProps = {
  projects: OptProject[]
}

function SandboxTableContent({ projects }: SandboxTableContentProps): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [confirmStatus, setConfirmStatus] = useState<{ status: ProjectStatus; project?: OptProject }>({
    status: undefined,
    project: undefined,
  })

  const { mutateAsync: deleteMutate } = useOptProjectsDeleteMutation()

  function onDelete(id: number): void {
    deleteMutate(id).then(
      () => {
        alertStore.info(t(`The project was deleted`))
      },
      (errorMessage) => {
        alertStore.error(`${errorMessage}`)
      }
    )
  }

  const tableData = projects.map((project) => {
    const timeValue = project.updated_at ?? project.created_at

    return {
      createdChanged: timeValue !== `-` ? formatDateTimeToLocalizedString(timeValue, true) : timeValue,
      name: project.display_name,
      type: getSandboxOptType(project.project_type),
      period: `${formatDateTimeToLocalizedString(project.start_time, true)} - ${formatDateTimeToLocalizedString(
        project.end_time,
        true
      )}`,
      status: getSandboxOptStatus(project.status),
      manage: (
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid>
            <NewButton
              disabled={project.status === `Creating` || project.status === `Creation Failed`}
              loading={project.status === `Creating`}
              label={
                project.status === `Finished`
                  ? t(`Show result`)
                  : project.status === `Creating`
                    ? t(`Creating...`)
                    : t(`Open`)
              }
              onClick={() => navigate(`/sandbox/${project.id}/`)}
            />
          </Grid>
          <Grid>
            {project.status !== `Creating` && project.status !== `Creation Failed` && (
              <NewButton
                iconButton
                icon="fal fa-copy"
                iconSize="large"
                onClick={() => setConfirmStatus({ status: `create`, project: project })}
                label={t(`Copy project`)}
              />
            )}
            {project.status !== `Creating` && project.status !== `Creation Failed` && (
              <NewButton
                iconButton
                variant="delete"
                iconSize="large"
                onClick={() => setConfirmStatus({ status: `delete`, project: project })}
                label={t(`Delete project`)}
              />
            )}
          </Grid>
        </Grid>
      ),
    }
  })

  const columns: LegacyColumn[] = [
    {
      accessor: `createdChanged`,
      Header: t(`Created/Changed`),
    },
    {
      accessor: `name`,
      Header: t(`Name`),
    },
    {
      accessor: `type`,
      Header: t(`Type of sandbox optimization`),
    },
    {
      accessor: `period`,
      Header: t(`Optimization period`),
    },
    {
      accessor: `status`,
      Header: t(`Status`),
    },
    {
      accessor: `manage`,
      Header: t(`Manage`),
    },
  ]

  return (
    <Grid container size={{ lg: 12 }}>
      <div className="w-full">
        <DefaultTable data={tableData} columns={columns} emptyTableText={t(`No previous projects found`)} />
      </div>

      <ConfirmDialog
        open={confirmStatus.status === `delete`}
        onClose={() => {
          setConfirmStatus({ status: undefined, project: undefined })
        }}
        onConfirm={() => {
          confirmStatus.project && onDelete(confirmStatus.project.id)
          setConfirmStatus({ status: undefined, project: undefined })
        }}
        textObject={{
          title: t(`Are you sure you want to delete the selected project?`),
          text: t(`You cannot reverse this action.`),
        }}
      />

      {confirmStatus.project && (
        <CreateSandboxPopover
          open={confirmStatus.status === `create`}
          closePopover={() => setConfirmStatus({ status: undefined, project: undefined })}
          defaultValues={{ ...confirmStatus.project, display_name: confirmStatus.project.display_name + ` copy` }}
        />
      )}
    </Grid>
  )
}

export default function SandboxTable(): ReactElement {
  const [period, setPeriod] = useState({
    startTime: moment().subtract(10, `d`).startOf(`day`),
    endTime: moment().endOf(`day`),
  })
  const { data: projects, status } = useOptProjects({
    start_time: period.startTime.format(),
    end_time: period.endTime.format(),
  })

  return (
    <Grid container direction="row" justifyContent="flex-end">
      <Grid>
        <HeaderPeriodPicker period={period} handlePeriodChange={setPeriod} />
      </Grid>
      <StatusComponent status={[status]} data={{ projects }} Component={SandboxTableContent} />
    </Grid>
  )
}
